import React, { useEffect, useState, useMemo } from 'react';
import productService from '../services/productService';
import ingredientService from '../services/ingredientService';
import ingredientInventoryService from '../services/ingredientInventoryService';
import { useLanguage, t } from '../context/LanguageContext';

const LB_TO_KG = 0.453592;

const PRINT_STYLES = `
  @media print {
    nav.navbar, nav.navbar + div { display: none !important; }
    @page { size: letter portrait; margin: 18mm 20mm; }
    body { padding: 0 !important; margin: 0 !important; }
  }
`;

function ProductionSchedulePage() {
  const { lang } = useLanguage();
  const [products, setProducts] = useState([]);
  const [prices, setPrices] = useState({});       // { [ingredientId]: pricePerKg }
  const [stock, setStock] = useState({});         // { [ingredientId]: stockKg }
  const [recipeCache, setRecipeCache] = useState({}); // { [productId]: [{ingredientId, expectedQuantityKg, Ingredient}] }
  const [plan, setPlan] = useState([]);           // [{ productId, name, qty }]
  const [selectedProductId, setSelectedProductId] = useState('');
  const [planQty, setPlanQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [addingId, setAddingId] = useState(null); // productId being fetched
  const [showPrices, setShowPrices] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [prods, currentPrices, stockRows] = await Promise.all([
          productService.getProducts(),
          ingredientService.getCurrentPrices(),
          ingredientInventoryService.getStock({}),
        ]);
        setProducts(prods.filter(p => p.isActive !== false));
        const priceMap = {};
        currentPrices.forEach(({ ingredientId, pricePerKg }) => {
          priceMap[ingredientId] = Number(pricePerKg);
        });
        setPrices(priceMap);
        const stockMap = {};
        (Array.isArray(stockRows) ? stockRows : []).forEach(r => {
          stockMap[r.ingredientId] = Number(r.stockKg || 0);
        });
        setStock(stockMap);
      } catch (err) {
        console.error('Error loading production schedule data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Add a product to the plan (fetch recipe if not cached)
  const addToPlan = async () => {
    if (!selectedProductId || !planQty || planQty < 1) return;
    const id = Number(selectedProductId);
    setAddingId(id);
    try {
      let recipe = recipeCache[id];
      if (!recipe) {
        const raw = await productService.getProductRecipe(id);
        recipe = Array.isArray(raw) ? raw : (raw.allIngredients || []);
        setRecipeCache(c => ({ ...c, [id]: recipe }));
      }
      const exists = plan.find(p => p.productId === id);
      if (exists) {
        setPlan(prev => prev.map(p => p.productId === id ? { ...p, qty: p.qty + Number(planQty) } : p));
      } else {
        const product = products.find(p => p.id === id);
        setPlan(prev => [...prev, { productId: id, name: product.name, qty: Number(planQty) }]);
      }
    } catch (err) {
      console.error('Error fetching recipe:', err);
    } finally {
      setAddingId(null);
      setSelectedProductId('');
      setPlanQty(1);
    }
  };

  const updateQty = (productId, newQty) => {
    const qty = parseInt(newQty, 10);
    if (isNaN(qty) || qty < 1) return;
    setPlan(prev => prev.map(p => p.productId === productId ? { ...p, qty } : p));
  };

  const removeFromPlan = (productId) => {
    setPlan(prev => prev.filter(p => p.productId !== productId));
  };

  // Aggregate ingredient requirements across all plan items
  const ingredientSummary = useMemo(() => {
    const map = {};
    plan.forEach(({ productId, qty }) => {
      (recipeCache[productId] || []).forEach(({ ingredientId, expectedQuantityKg, Ingredient }) => {
        if (!map[ingredientId]) {
          map[ingredientId] = {
            ingredientId,
            name: Ingredient.name,
            rawMaterialCode: Ingredient.rawMaterialCode || null,
            totalKg: 0,
            pricePerKg: prices[ingredientId] ?? null,
            stockKg: stock[ingredientId] ?? null,
            caseSize: Ingredient.caseSize != null ? Number(Ingredient.caseSize) : null,
            caseSizeUnit: Ingredient.caseSizeUnit || 'kg',
            caseLabel: Ingredient.caseLabel || null,
          };
        }
        map[ingredientId].totalKg += expectedQuantityKg * qty;
      });
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }, [plan, recipeCache, prices, stock]);

  // Returns cases needed, stock-in-cases, and display strings for an ingredient row
  const caseInfo = (ing) => {
    if (ing.caseSize == null || ing.caseSize <= 0) return null;
    const caseSizeKg = ing.caseSizeUnit === 'lb' ? ing.caseSize * LB_TO_KG : ing.caseSize;
    const needed = Math.ceil(ing.totalKg / caseSizeKg);
    const label = ing.caseLabel || (ing.caseSizeUnit === 'lb' ? 'case' : 'bag');
    const eaStr = `${Number(ing.caseSize).toLocaleString()} ${ing.caseSizeUnit} ea.`;
    const stockCases = ing.stockKg != null ? Math.floor(ing.stockKg / caseSizeKg) : null;
    return { needed, label, eaStr, stockCases };
  };

  const grandTotal = useMemo(
    () => ingredientSummary.reduce((sum, i) => {
      if (i.pricePerKg == null) return sum;
      return sum + i.totalKg * i.pricePerKg;
    }, 0),
    [ingredientSummary]
  );

  const planSummaryText = plan.map(p => `${p.name} ×${p.qty}`).join('  |  ');
  const printDate = new Date().toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  if (loading) {
    return <div className="container mt-5"><p>{t('Loading…', 'Cargando…', lang)}</p></div>;
  }

  return (
    <>
      <style>{PRINT_STYLES}</style>

      {/* Print-only header */}
      <div className="d-none d-print-block mb-3">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 4 }}>
          <h4 style={{ margin: 0 }}>{t('Production Schedule', 'Programa de Producción', lang)}</h4>
          <span style={{ fontSize: 12, color: '#555' }}>{printDate}</span>
        </div>
        {planSummaryText && (
          <p style={{ fontSize: 12, color: '#555', margin: '4px 0 0' }}>
            <strong>{t('Plan:', 'Plan:', lang)}</strong> {planSummaryText}
          </p>
        )}
        <hr style={{ margin: '8px 0 12px' }} />
      </div>

      <div className="container-fluid mt-4 d-print-none" style={{ maxWidth: 1200 }}>
        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
          <h4 className="mb-0">{t('Production Schedule', 'Programa de Producción', lang)}</h4>
          <div className="d-flex align-items-center gap-2">
            <div className="form-check form-switch mb-0 d-flex align-items-center gap-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="togglePrices"
                checked={showPrices}
                onChange={e => setShowPrices(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <label className="form-check-label small text-muted" htmlFor="togglePrices">
                {t('Show prices', 'Mostrar precios', lang)}
              </label>
            </div>
            <button
              className="btn btn-outline-secondary"
              onClick={() => window.print()}
              disabled={ingredientSummary.length === 0}
            >
              {t('Print', 'Imprimir', lang)}
            </button>
          </div>
        </div>
      </div>

      <div className="container-fluid d-print-none" style={{ maxWidth: 1200 }}>
        <div className="row g-4">
          {/* ── Left: Plan builder ── */}
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-header fw-semibold">{t('Plan', 'Plan', lang)}</div>
              <div className="card-body">
                {/* Add product */}
                <div className="mb-3">
                  <select
                    className="form-select form-select-sm mb-2"
                    value={selectedProductId}
                    onChange={e => setSelectedProductId(e.target.value)}
                  >
                    <option value="">{t('Select product…', 'Seleccionar producto…', lang)}</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">{t('Qty', 'Cant.', lang)}</span>
                    <input
                      type="number"
                      className="form-control"
                      min={1}
                      value={planQty}
                      onChange={e => setPlanQty(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addToPlan()}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={addToPlan}
                      disabled={!selectedProductId || addingId != null}
                    >
                      {addingId != null ? (
                        <span className="spinner-border spinner-border-sm" />
                      ) : t('+ Add', '+ Agregar', lang)}
                    </button>
                  </div>
                </div>

                {/* Plan list */}
                {plan.length === 0 ? (
                  <p className="text-muted small">{t('No products added yet.', 'No se han agregado productos.', lang)}</p>
                ) : (
                  <table className="table table-sm mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t('Product', 'Producto', lang)}</th>
                        <th style={{ width: 80 }}>{t('Qty', 'Cant.', lang)}</th>
                        <th style={{ width: 36 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.map(item => (
                        <tr key={item.productId}>
                          <td className="align-middle small">{item.name}</td>
                          <td className="align-middle">
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              min={1}
                              value={item.qty}
                              onChange={e => updateQty(item.productId, e.target.value)}
                            />
                          </td>
                          <td className="align-middle">
                            <button
                              className="btn btn-link text-danger p-0"
                              onClick={() => removeFromPlan(item.productId)}
                              title={t('Remove', 'Eliminar', lang)}
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* ── Right: Ingredient requirements ── */}
          <div className="col-md-8">
            <div className="card h-100">
              <div className="card-header fw-semibold">{t('Ingredient Requirements', 'Requerimientos de Ingredientes', lang)}</div>
              <div className="card-body p-0">
                {ingredientSummary.length === 0 ? (
                  <p className="text-muted small p-3">
                    {t('Add products to the plan to see ingredient requirements.', 'Agrega productos al plan para ver los requerimientos de ingredientes.', lang)}
                  </p>
                ) : (
                  <table className="table table-sm mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t('Ingredient', 'Ingrediente', lang)}</th>
                        <th className="text-end">{t('Required (kg)', 'Requerido (kg)', lang)}</th>
                        <th className="text-end">{t('Cases Needed', 'Cajas Necesarias', lang)}</th>
                        <th className="text-end">{t('In Stock', 'En Stock', lang)}</th>
                        {showPrices && <th className="text-end">{t('Price / kg', 'Precio / kg', lang)}</th>}
                        {showPrices && <th className="text-end">{t('Est. Cost', 'Costo Est.', lang)}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {ingredientSummary.map(ing => {
                        const cost = ing.pricePerKg != null ? ing.totalKg * ing.pricePerKg : null;
                        const ci = caseInfo(ing);
                        const stockKg = ing.stockKg;
                        const stockColor = stockKg == null ? '' : stockKg >= ing.totalKg ? 'text-success' : stockKg > 0 ? 'text-warning fw-semibold' : 'text-danger fw-semibold';
                        return (
                          <tr key={ing.name}>
                            <td className="align-middle">
                              {ing.name}
                              {ing.rawMaterialCode && (
                                <span className="badge text-bg-light text-dark border ms-1" style={{ fontSize: '0.7rem' }}>{ing.rawMaterialCode}</span>
                              )}
                            </td>
                            <td className="align-middle text-end">{ing.totalKg.toFixed(3)}</td>
                            <td className="align-middle text-end">
                              {ci ? (
                                <>
                                  <span className="fw-semibold">{ci.needed} {ci.label}</span>
                                  <br /><span className="text-muted" style={{ fontSize: '0.75rem' }}>{ci.eaStr}</span>
                                </>
                              ) : '—'}
                            </td>
                            <td className={`align-middle text-end ${stockColor}`}>
                              {stockKg != null ? (
                                <>
                                  {stockKg.toFixed(1)} kg
                                  {ci?.stockCases != null && (
                                    <><br /><span style={{ fontSize: '0.8rem' }}>{ci.stockCases} {ci.label}</span></>
                                  )}
                                </>
                              ) : '—'}
                            </td>
                            {showPrices && (
                              <td className="align-middle text-end text-muted">
                                {ing.pricePerKg != null ? `$${ing.pricePerKg.toFixed(4)}` : '—'}
                              </td>
                            )}
                            {showPrices && (
                              <td className="align-middle text-end fw-semibold">
                                {cost != null ? `$${cost.toFixed(2)}` : '—'}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan={showPrices ? 5 : 3} className="text-end fw-bold">{t('Grand Total', 'Total General', lang)}</td>
                        {showPrices && <td className="text-end fw-bold">${grandTotal.toFixed(2)}</td>}
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print-only ingredient table */}
      <div className="d-none d-print-block">
        {ingredientSummary.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #333' }}>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>{t('Ingredient', 'Ingrediente', lang)}</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>{t('Required (kg)', 'Requerido (kg)', lang)}</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>{t('Cases Needed', 'Cajas Necesarias', lang)}</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>{t('In Stock', 'En Stock', lang)}</th>
                {showPrices && <th style={{ textAlign: 'right', padding: '4px 8px' }}>{t('Price / kg', 'Precio / kg', lang)}</th>}
                {showPrices && <th style={{ textAlign: 'right', padding: '4px 8px' }}>{t('Est. Cost', 'Costo Est.', lang)}</th>}
              </tr>
            </thead>
            <tbody>
              {ingredientSummary.map(ing => {
                const cost = ing.pricePerKg != null ? ing.totalKg * ing.pricePerKg : null;
                const ci = caseInfo(ing);
                const stockKg = ing.stockKg;
                return (
                  <tr key={ing.name} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '4px 8px' }}>
                      {ing.name}
                      {ing.rawMaterialCode && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: '#666', fontWeight: 'normal' }}>({ing.rawMaterialCode})</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', padding: '4px 8px' }}>{ing.totalKg.toFixed(3)}</td>
                    <td style={{ textAlign: 'right', padding: '4px 8px' }}>
                      {ci ? `${ci.needed} ${ci.label} (${ci.eaStr})` : '—'}
                    </td>
                    <td style={{ textAlign: 'right', padding: '4px 8px' }}>
                      {stockKg != null
                        ? ci?.stockCases != null
                          ? `${stockKg.toFixed(1)} kg / ${ci.stockCases} ${ci.label}`
                          : `${stockKg.toFixed(1)} kg`
                        : '—'}
                    </td>
                    {showPrices && (
                      <td style={{ textAlign: 'right', padding: '4px 8px', color: '#666' }}>
                        {ing.pricePerKg != null ? `$${ing.pricePerKg.toFixed(4)}` : '—'}
                      </td>
                    )}
                    {showPrices && (
                      <td style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 600 }}>
                        {cost != null ? `$${cost.toFixed(2)}` : '—'}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #333' }}>
                <td colSpan={showPrices ? 5 : 3} style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700 }}>{t('Grand Total', 'Total General', lang)}</td>
                {showPrices && <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700 }}>${grandTotal.toFixed(2)}</td>}
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </>
  );
}

export default ProductionSchedulePage;
